import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Typography, Avatar, message, Spin, Table, Input, Tooltip, Button, Space } from 'antd';
import dayjs from 'dayjs';
import ButtonState from '../../components/Components/Buttons/ButtonState';
import { primaryShades } from '../../theme/theme';
import { SearchOutlined, InfoCircleOutlined, CloseOutlined, EditOutlined, CheckOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { statusMapping } from "../../services/utils/ar3dViewer";
import { isVariantClickable, canViewNotes } from '../services/Utils';
import NoteViewerModal from '../modal/NoteViewerModal';
import ImagePositioningModal from '../modal/ImagePositioningModal';
import { db } from "../../data/base";
import { secureGetItem } from '../../data/utils';
import HeaderComponent from '../../components/Components/Headers/HeaderComponent';
import CustomTooltip from "../../components/Components/Modals/CustomTooltip";
import HoverImageZoom from '../../components/Components/Images/HoverImageZoom';
import Publish3DAssetsModal from '../modal/Publish3DAssetsModal';
import { IconButton } from '../../components/Components/Buttons/Buttons';
import { useSelector } from 'react-redux';
import '../../styles/FrameValidation/Products.css';

// 1. Vai direttamente alle Variants  
// 2. Cerca tutte le varianti con catalogOrdersRef = ordine.id
// 3. Dalle varianti, prendi tutti i productRef unici
// 4. Carica i prodotti usando questi productRef
// 5. Organizza le varianti sotto ogni prodotto

// Importazione dell'icona Orders personalizzata
import ARShadesOrdersIcon from '../../assets/icon/ARShades Orders icon2.png';

function Products() {
    const { brandId } = useParams();
    const [brand, setBrand] = useState(null);
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [variants, setVariants] = useState({});
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [profiles, setProfiles] = useState({});
    const [isManualExpansion, setIsManualExpansion] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editingName, setEditingName] = useState("");
    const [isOrderMode, setIsOrderMode] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [isImageModalVisible, setIsImageModalVisible] = useState(false);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const navigate = useNavigate();

    // Prendiamo il tema dallo store Redux
    const theme = useSelector(state => state.theme);
    const PRIMARY_200 = theme.primary.primary200;

    const handleBack = () => {
        navigate('/framevalidation');
    };

    // Funzione per iniziare la modifica del nome
    const handleStartEditName = () => {
        setEditingName(brand?.nameBrand || "");
        setIsEditingName(true);
    };

    // Funzione per salvare il nuovo nome
    const handleSaveName = async () => {
        if (!currentOrderId || !editingName.trim()) {
            message.error("Nome non valido");
            return;
        }

        try {
            const orderRef = doc(db, "CatalogOrders", currentOrderId);
            await updateDoc(orderRef, {
                orderName: editingName.trim()
            });

            // Aggiorna lo stato locale del brand
            setBrand(prevBrand => ({
                ...prevBrand,
                nameBrand: editingName.trim()
            }));

            setIsEditingName(false);
            message.success("Nome aggiornato con successo!");
        } catch (error) {
            console.error("Errore nell'aggiornamento del nome:", error);
            message.error("Errore nell'aggiornamento del nome");
        }
    };

    // Funzione per annullare la modifica
    const handleCancelEdit = () => {
        setIsEditingName(false);
        setEditingName("");
    };

    // Funzione per gestire la selezione del file e aprire la modale
    const handleImageSelection = (file, orderId) => {
        setSelectedImageFile(file);
        setIsImageModalVisible(true);
    };

    // Funzione per gestire il caricamento dell'immagine finale
    const handleImageUpload = async (file, orderId) => {
        setUploadingImage(true);
        try {
            const storage = getStorage();
            const imageRef = ref(storage, `CatalogOrders/${orderId}/image/${file.name}`);

            // Carica l'immagine
            const snapshot = await uploadBytes(imageRef, file);

            // Ottieni l'URL di download
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Aggiorna il documento Firebase con l'URL dell'immagine
            const orderRef = doc(db, "CatalogOrders", orderId);
            await updateDoc(orderRef, {
                image: downloadURL
            });

            // Aggiorna lo stato locale del brand per mostrare l'immagine caricata
            setBrand(prevBrand => ({
                ...prevBrand,
                urlLogo: downloadURL
            }));

            message.success("Immagine caricata con successo!");
        } catch (error) {
            console.error("Errore nel caricamento dell'immagine:", error);
            message.error("Errore nel caricamento dell'immagine");
        } finally {
            setUploadingImage(false);
        }
    };

    // Funzioni per gestire la modale di posizionamento immagine
    const handleImageModalConfirm = async (croppedFile) => {
        if (croppedFile && currentOrderId) {
            await handleImageUpload(croppedFile, currentOrderId);
            handleImageModalCancel();
        }
    };

    const handleImageModalCancel = () => {
        setIsImageModalVisible(false);
        setSelectedImageFile(null);
    };



    // Funzione per rimuovere l'immagine direttamente (bottone delete)
    const handleDirectRemoveImage = async () => {
        if (!currentOrderId) return;

        try {
            const orderRef = doc(db, "CatalogOrders", currentOrderId);
            await updateDoc(orderRef, {
                image: null
            });

            // Aggiorna lo stato locale per rimuovere l'immagine
            setBrand(prevBrand => ({
                ...prevBrand,
                urlLogo: null
            }));

            message.success("Image removed successfully!");
        } catch (error) {
            console.error("Error removing image:", error);
            message.error("Error removing image");
        }
    };

    const [processedImages, setProcessedImages] = useState({});

    // Immagine placeholder per errori CORS e 404 - usa la stessa logica di ARSCatalog
    const createPlaceholderImage = (width = 400, height = 400) => {
        return 'data:image/svg+xml;base64,' + btoa(`
<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <rect width="${width}" height="${height}" fill="#f5f5f5" stroke="#d9d9d9" stroke-width="2"/>
  <g transform="translate(${width / 2}, ${height / 2})">
    <text x="0" y="-10" font-family="Arial, sans-serif" font-size="${Math.min(width, height) * 0.08}" fill="#999" text-anchor="middle">No Image</text>
    <text x="0" y="15" font-family="Arial, sans-serif" font-size="${Math.min(width, height) * 0.06}" fill="#999" text-anchor="middle">Available</text>
  </g>
</svg>
`);
    };

    const DEFAULT_PLACEHOLDER = createPlaceholderImage();

    // Funzione per rimuovere lo sfondo bianco dalle immagini con gestione fallback
    const removeWhiteBackground = (imgUrl, id) => {
        if (!imgUrl) return null;
        if (processedImages[id]) return processedImages[id];

        try {
            // Crea un elemento immagine per caricare l'URL
            const img = new Image();
            img.crossOrigin = "Anonymous";

            img.onload = () => {
                try {
                    // Crea un canvas per elaborare l'immagine
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Imposta le dimensioni del canvas
                    canvas.width = img.width;
                    canvas.height = img.height;

                    // Disegna l'immagine nel canvas
                    ctx.drawImage(img, 0, 0);

                    // Ottieni i dati dei pixel dell'immagine
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;

                    // Rimuovi lo sfondo bianco rendendo trasparenti i pixel bianchi
                    for (let i = 0; i < data.length; i += 4) {
                        const r = data[i];
                        const g = data[i + 1];
                        const b = data[i + 2];

                        // Se il pixel è vicino al bianco, rendilo trasparente
                        if (r > 240 && g > 240 && b > 240) {
                            data[i + 3] = 0; // Imposta il canale alpha a 0 (trasparente)
                        }
                    }

                    // Aggiorna l'immagine nel canvas
                    ctx.putImageData(imageData, 0, 0);

                    // Converti il canvas in un URL di dati
                    const processedImageUrl = canvas.toDataURL('image/png');

                    // Aggiorna lo stato delle immagini elaborate
                    setProcessedImages(prev => ({
                        ...prev,
                        [id]: processedImageUrl
                    }));
                } catch (canvasError) {
                    console.warn(`Canvas processing failed for image ${imgUrl}:`, canvasError.message);
                    // In caso di errore del canvas, usa l'immagine originale
                    setProcessedImages(prev => ({
                        ...prev,
                        [id]: imgUrl
                    }));
                }
            };

            img.onerror = (error) => {
                console.warn(`Failed to load image ${imgUrl}:`, error);
                // In caso di errore di caricamento, usa il placeholder
                setProcessedImages(prev => ({
                    ...prev,
                    [id]: DEFAULT_PLACEHOLDER
                }));
            };

            img.src = imgUrl;

            // Restituisci l'URL originale immediatamente, l'elaborazione avverrà in background
            return imgUrl;
        } catch (error) {
            console.warn(`Error processing image ${imgUrl}:`, error.message);
            return imgUrl; // Restituisci l'URL originale in caso di errore
        }
    };

    // Effetto che viene eseguito quando cambia il brandId. Può essere un brand o un order ID.
    useEffect(() => {
        if (!brandId) {
            message.error("Invalid ID");
            setLoading(false);
            return;
        }

        const extractedId = brandId.startsWith("id=") ? brandId.replace("id=", "") : brandId;

        const fetchDataAndModels = async () => {
            setLoading(true);
            try {
                // Prima prova a recuperare come MainBrand
                let brandRef = doc(db, "MainBrands", extractedId);
                let brandSnap = await getDoc(brandRef);
                let isOrderMode = false;
                let orderData = null;

                if (!brandSnap.exists()) {
                    // Se non è un brand, prova come CatalogOrder
                    const orderRef = doc(db, "CatalogOrders", extractedId);
                    const orderSnap = await getDoc(orderRef);

                    if (orderSnap.exists()) {
                        isOrderMode = true;
                        orderData = orderSnap.data();

                        // Imposta gli stati per la modalità ordine
                        setIsOrderMode(true);
                        setCurrentOrderId(extractedId);

                        // Crea un brand fittizio per l'ordine
                        const mockBrand = {
                            nameBrand: orderData.orderName || "Undefined",
                            urlLogo: orderData.image || null // Usa l'immagine dell'ordine se disponibile
                        };
                        setBrand(mockBrand);
                    } else {
                        message.error("Order or Brand not found");
                        setLoading(false);
                        return;
                    }
                } else {
                    const brandData = brandSnap.data();
                    setBrand(brandData);
                }

                // Recupera i profili
                await fetchProfiles();

                let filteredModels = [];
                const variantsData = {};

                if (isOrderMode && orderData) {
                    // Nuova modalità ordine: recupera direttamente le varianti tramite catalogOrdersRef
                    // e poi recupera i prodotti tramite productRef per mostrare tutte le varianti del catalogOrder
                    const variantsQuery = query(
                        collection(db, "Variants"),
                        where("catalogOrdersRef", "==", extractedId)
                    );
                    const variantsSnapshot = await getDocs(variantsQuery);
                    const allVariants = variantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    if (allVariants.length > 0) {
                        // Filtra solo varianti non pubblicate
                        const nonPublishedVariants = allVariants.filter(variant => variant.status !== "Pubblicato");

                        // Raccogli tutti i productRef unici dalle varianti
                        const uniqueProductRefs = [...new Set(nonPublishedVariants.map(variant => variant.productRef))];

                        // Recupera i prodotti dai productRef
                        const productsPromises = uniqueProductRefs.map(async (productRef) => {
                            try {
                                const productDoc = doc(db, "Products", productRef);
                                const productSnapshot = await getDoc(productDoc);
                                if (productSnapshot.exists()) {
                                    return { id: productSnapshot.id, ...productSnapshot.data() };
                                }
                                return null;
                            } catch (error) {
                                console.error(`Errore nel recupero del prodotto ${productRef}:`, error);
                                return null;
                            }
                        });

                        const allProducts = (await Promise.all(productsPromises)).filter(product => product !== null);

                        // Applica mapping dello status e ordinamento ai prodotti
                        filteredModels = allProducts
                            .map(product => ({
                                ...product,
                                status: statusMapping[product.status] || product.status
                            }))
                            .sort((a, b) => {
                                const order = ['Published', 'In Publication', 'Client Rev.', 'Spaarkly Rev.', 'Modelist Rev.', 'Incomplete'];
                                return order.indexOf(a.status) - order.indexOf(b.status);
                            });

                        // Organizza le varianti per prodotto
                        filteredModels.forEach(product => {
                            const productVariants = nonPublishedVariants.filter(variant => variant.productRef === product.id);
                            variantsData[product.id] = productVariants;
                        });

                        // Filtra i prodotti che hanno almeno una variante
                        filteredModels = filteredModels.filter(product => {
                            const productVariants = variantsData[product.id] || [];
                            return productVariants.length > 0;
                        });
                    }
                } else {
                    // Modalità brand: comportamento originale
                    const q = query(
                        collection(db, "Products"),
                        where("mainBrandRef", "==", extractedId)
                    );

                    const querySnapshot = await getDocs(q);

                    filteredModels = querySnapshot.docs
                        .map(doc => ({ id: doc.id, ...doc.data() }))
                        .map(product => ({
                            ...product,
                            status: statusMapping[product.status] || product.status
                        }))
                        .sort((a, b) => {
                            const order = ['Published', 'In Publication', 'Client Rev.', 'Spaarkly Rev.', 'Modelist Rev.', 'Incomplete'];
                            return order.indexOf(a.status) - order.indexOf(b.status);
                        });

                    // Recupera le varianti per ciascun modello
                    await Promise.all(filteredModels.map(async (product) => {
                        const productVariants = await fetchVariants(product.id);
                        variantsData[product.id] = productVariants;
                    }));

                    // Filtra i prodotti che hanno almeno una variante non pubblicata
                    filteredModels = filteredModels.filter(product => {
                        const productVariants = variantsData[product.id] || [];
                        const nonPublishedVariants = productVariants.filter(variant => variant.status !== "Pubblicato");
                        return nonPublishedVariants.length > 0;
                    });
                }

                setVariants(variantsData);
                setModels(filteredModels);
            } catch (error) {
                console.error("Error retrieving data:", error);
                message.error("Error retrieving data");
            } finally {
                setLoading(false);
            }
        };

        fetchDataAndModels();
    }, [brandId, db]);

    // Funzione che recupera le varianti di un prodotto specifico dal database Firebase.
    const fetchVariants = async (productId) => {
        try {
            const variantsQuery = query(collection(db, "Variants"), where("productRef", "==", productId));
            const querySnapshot = await getDocs(variantsQuery);
            const order = ['In Pubblicazione', 'Rev. Cliente', 'Rev. Spaarkly', 'Rev. Modellista', 'Incompleto'];
            const productVariants = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));

            return productVariants;
        } catch (error) {
            console.error("Errore nel recupero delle varianti:", error);
            return [];
        }
    };

    // Funzione per recuperare i dati dei profili dalla collezione 'Profiles'
    const fetchProfiles = async () => {
        try {
            const profilesQuery = query(collection(db, "Profiles"));
            const querySnapshot = await getDocs(profilesQuery);
            const profilesData = {};

            querySnapshot.docs.forEach(doc => {
                const profileData = doc.data();
                // Usa l'ID del documento come chiave (che è l'email)
                profilesData[doc.id] = {
                    firstName: profileData.firstName || '',
                    lastName: profileData.lastName || '',
                    fullName: `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim()
                };
            });

            setProfiles(profilesData);
        } catch (error) {
            console.error("Errore nel recupero dei profili:", error);
        }
    };

    // Funzione che gestisce il click su un prodotto, reindirizzando l'utente alla pagina di validazione.
    const handleProductClick = (productId) => {
        navigate(`/framevalidation/${brandId}/${productId}`);
    };

    // Funzione che gestisce il click su una variante, reindirizzando l'utente alla pagina di validazione.
    const handleVariantClick = (brandId, productId, variantId, variantStatus) => {
        const role = secureGetItem("role");
        if (isVariantClickable(role, variantStatus)) {
            navigate(`/framevalidation/${brandId}/${productId}/${variantId}`);
        } else {
            message.warning("You don't have permission to access this variant.");
        }
    };

    // Definizione delle colonne della tabella prodotti.
    const columns = [
        // La colonna del '+' viene gestita automaticamente da Ant Design Table con la proprietà expandable
        // Non è necessario definirla esplicitamente qui
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Image</span>
                    <CustomTooltip title="Image of the product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'imgUrl',
            key: 'imgUrl',
            render: (text, record) => {
                const imgSrc = processedImages[record.id] || text;

                if (!processedImages[record.id] && text) {
                    removeWhiteBackground(text, record.id);
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        {imgSrc ? (
                            <HoverImageZoom
                                src={imgSrc}
                                alt="Product"
                                style={{ width: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)', objectFit: 'contain' }}
                            />
                        ) : (
                            <div style={{
                                width: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)',
                                height: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: '#999',
                                fontSize: '0.75rem'
                            }}>
                                No Image
                            </div>
                        )}
                    </div>
                );
            },
            align: 'center',
            width: '8%',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Name</span>
                    <CustomTooltip title="Name of the model">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'modelName',
            sorter: (a, b) => a.modelName.localeCompare(b.modelName),
            key: 'modelName',
            render: (text, record) => (
                <span
                    onClick={() => handleProductClick(record.id)}
                    style={{ cursor: 'pointer', color: 'black', textDecoration: 'underline' }}
                >
                    {highlightMatch(text, appliedSearchTerm)}
                </span>
            ),
            align: 'left',
            width: '20%',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>N.R. Variant Available</span>
                    <CustomTooltip title="Number of variants available for this product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'variantCount',
            render: (_, product) => {
                // Esclude le varianti con stato "Pubblicato"
                const count = variants[product.id] ?
                    variants[product.id].filter(variant => variant.status !== "Pubblicato").length : 0;
                return (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: "black",
                        borderRadius: "50%",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        textAlign: 'center',
                        margin: '0 auto'
                    }}>
                        {count}
                    </div>
                );
            },
            align: 'center',
            width: '12%',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Variant Status</span>
                    <CustomTooltip title="Status of the variants">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'variantStatus',
            render: (_, product) => {
                // Filtra le varianti escludendo quelle con stato "Pubblicato"
                const filteredVariants = variants[product.id] ?
                    variants[product.id].filter(variant => variant.status !== "Pubblicato") : [];

                if (filteredVariants.length === 0) {
                    return (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: "#ccc",
                            color: "#fff",
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            fontSize: "14px",
                            fontWeight: "bold",
                            textAlign: 'center',
                            margin: '0 auto'
                        }}>
                            0
                        </div>
                    );
                }
                const statusCounts = filteredVariants.reduce((acc, variant) => {
                    acc[variant.status] = (acc[variant.status] || 0) + 1;
                    return acc;
                }, {});

                return (
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', textAlign: 'center', width: '100%' }}>
                        {Object.entries(statusCounts).map(([status, count]) => {
                            const isModelistRev = status === "Modelist Rev." || status === "Rev. Modellista";
                            const isIncomplete = status === "Incomplete" || status === "Incompleto";

                            // Traduzione degli stati per i tooltip
                            const statusTranslations = {
                                "In Pubblicazione": "In Publication",
                                "Rev. Modellista": "Modelist Rev.",
                                "Rev. Cliente": "Client Rev.",
                                "Rev. Spaarkly": "Spaarkly Rev.",
                                "Incompleto": "Incomplete"
                            };
                            const translatedStatus = statusTranslations[status] || status;

                            return (
                                <div key={status} style={{
                                    display: 'inline-block',
                                    margin: '0 2px'
                                }}>
                                    <Tooltip title={translatedStatus} color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                        <ButtonState
                                            state={status}
                                            isCircle={true}
                                            text={`${count}`}
                                        />
                                    </Tooltip>
                                </div>
                            );
                        })}
                    </div>
                );
            },
            align: 'center',
            width: '20%',
        },
        // Colonna "Assigned?" visibile solo per Admin e ModellerSupervisor
        ...(secureGetItem("role") === "Admin" || secureGetItem("role") === "ModellerSupervisor" ? [{
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Assigned?</span>
                    <CustomTooltip title="Shows who is assigned to the variants">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'assigned',
            render: (_, product) => {
                // Filtra le varianti escludendo quelle con stato "Pubblicato"
                const filteredVariants = variants[product.id] ?
                    variants[product.id].filter(variant => variant.status !== "Pubblicato") : [];

                if (filteredVariants.length === 0) {
                    return <span style={{ color: 'gray', textAlign: 'center', display: 'block' }}>-</span>;
                }

                // Raccoglie tutte le assegnazioni uniche
                const assignedEmails = filteredVariants
                    .filter(variant => variant.assignedTo && variant.assignedTo.trim() !== '')
                    .map(variant => variant.assignedTo);

                // Se non ci sono assegnazioni, mostra "No"
                if (assignedEmails.length === 0) {
                    return (
                        <div style={{
                            textAlign: 'center',
                            color: 'black'
                        }}>
                            No
                        </div>
                    );
                }

                // Rimuove duplicati e crea un array di assegnazioni uniche
                const uniqueAssignments = [...new Set(assignedEmails)];

                // Crea i cerchietti con le iniziali
                return (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '4px',
                        flexWrap: 'wrap'
                    }}>
                        {uniqueAssignments.map((email, index) => {
                            const profile = profiles[email];
                            let initials = "??";
                            let fullName = email;

                            if (profile) {
                                const firstName = profile.firstName || '';
                                const lastName = profile.lastName || '';
                                initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
                                fullName = profile.fullName;
                            }

                            // Calcola quante varianti sono assegnate a questa persona
                            const assignedCount = filteredVariants.filter(v => v.assignedTo === email).length;

                            return (
                                <Tooltip key={index} title={`${fullName} (${assignedCount})`} color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                    <div style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        backgroundColor: primaryShades.primary300,
                                        color: '#fff',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        position: 'relative'
                                    }}>
                                        {initials}
                                        {assignedCount > 1 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '-5px',
                                                right: '-5px',
                                                backgroundColor: primaryShades.primary500,
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '16px',
                                                height: '16px',
                                                fontSize: '10px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}>
                                                {assignedCount}
                                            </div>
                                        )}
                                    </div>
                                </Tooltip>
                            );
                        })}

                        {/* Se ci sono varianti non assegnate, mostra un cerchio grigio con "?" */}
                        {filteredVariants.some(v => !v.assignedTo || v.assignedTo.trim() === '') && (
                            <Tooltip title="Unassigned variants" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                <div style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: '#F5F5F5',
                                    color: '#9c9090',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    position: 'relative',
                                }}>
                                    X
                                    {/* Conta le varianti non assegnate */}
                                    {(() => {
                                        const unassignedCount = filteredVariants.filter(v => !v.assignedTo || v.assignedTo.trim() === '').length;
                                        if (unassignedCount > 1) {
                                            return (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '-5px',
                                                    right: '-5px',
                                                    backgroundColor: '#9c9090',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: '16px',
                                                    height: '16px',
                                                    fontSize: '10px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    opacity: 0.6
                                                }}>
                                                    {unassignedCount}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                );
            },
            sorter: {
                compare: (a, b) => {
                    const variantsA = variants[a.id] ? variants[a.id].filter(v => v.status !== "Pubblicato") : [];
                    const variantsB = variants[b.id] ? variants[b.id].filter(v => v.status !== "Pubblicato") : [];

                    if (variantsA.length === 0 && variantsB.length === 0) return 0;
                    if (variantsA.length === 0) return -1;
                    if (variantsB.length === 0) return 1;

                    const allAssignedA = variantsA.every(v => v.assignedTo && v.assignedTo.trim() !== '');
                    const allAssignedB = variantsB.every(v => v.assignedTo && v.assignedTo.trim() !== '');

                    // Yes viene prima di No nell'ordinamento
                    return allAssignedB - allAssignedA;
                },
                multiple: 1
            },
            align: 'center',
            width: '10%',
        }] : []),
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span>Last Update</span>
                    <CustomTooltip title="Date of the last update">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'lastUpdate',
            sorter: (a, b) => {
                const aValue = a.lastUpdate ? dayjs(a.lastUpdate.toDate ? a.lastUpdate.toDate() : a.lastUpdate).valueOf() : 0;
                const bValue = b.lastUpdate ? dayjs(b.lastUpdate.toDate ? b.lastUpdate.toDate() : b.lastUpdate).valueOf() : 0;
                return aValue - bValue;
            },
            key: 'lastUpdate',
            render: (date) => date ? dayjs(date.toDate ? date.toDate() : date).format('DD/MM/YYYY HH:mm') : '-',
            align: 'right',
            width: '18%',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span>Progress</span>
                    <CustomTooltip title="Progress percentage based on variant statuses">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'progress',
            sorter: {
                compare: (a, b) => {
                    // Calculate progress percentages for sorting
                    const getProgressPercentage = (product) => {
                        if (!variants[product.id] || variants[product.id].length === 0) return 0;

                        const statusWeights = {
                            "In Pubblicazione": 100,
                            "Rev. Cliente": 75,
                            "Rev. Modellista": 25,
                            "Rev. Spaarkly": 50,
                            "Incompleto": 0
                        };

                        // Filtra le varianti escludendo quelle pubblicate per il calcolo
                        const nonPublishedVariants = variants[product.id].filter(variant => variant.status !== "Pubblicato");

                        let totalWeight = 0;
                        let variantCount = nonPublishedVariants.length;

                        nonPublishedVariants.forEach(variant => {
                            totalWeight += statusWeights[variant.status] || 0;
                        });

                        return variantCount > 0 ? totalWeight / variantCount : 0;
                    };

                    return getProgressPercentage(b) - getProgressPercentage(a);
                },
                multiple: 1
            },
            render: (_, product) => {
                // Calculate progress percentage based on variant statuses
                if (!variants[product.id] || variants[product.id].length === 0) {
                    return (
                        <div
                            style={{
                                backgroundColor: "white",
                                color: "#333",
                                padding: "clamp(0.2rem, 0.15rem + 0.25vw, 0.25rem) clamp(0.35rem, 0.3rem + 0.5vw, 0.5rem)",
                                borderRadius: "0.25rem",
                                fontSize: "clamp(0.7rem, 0.65rem + 0.15vw, 0.75rem)",
                                fontWeight: "bold",
                                maxWidth: "clamp(100px, 10rem, 130px)",
                                textAlign: "center",
                                marginLeft: 'auto',
                                marginRight: '15px',
                                position: "relative",
                                overflow: "hidden",
                                border: "1px solid #ddd"
                            }}
                        >
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "0%",
                                    height: "100%",
                                    backgroundColor: "#ccc",
                                    zIndex: 1,
                                    transition: "width 0.3s ease"
                                }}
                            ></div>
                            <span style={{ position: "relative", zIndex: 2 }}>0%</span>
                        </div>
                    );
                }

                const statusWeights = {
                    "In Pubblicazione": 100,
                    "Rev. Cliente": 75,
                    "Rev. Modellista": 25,
                    "Rev. Spaarkly": 50,
                    "Incompleto": 0
                };

                // Filtra le varianti escludendo quelle pubblicate per il calcolo
                const nonPublishedVariants = variants[product.id].filter(variant => variant.status !== "Pubblicato");

                let totalWeight = 0;
                let variantCount = nonPublishedVariants.length;

                nonPublishedVariants.forEach(variant => {
                    totalWeight += statusWeights[variant.status] || 0;
                });

                const progressPercentage = variantCount > 0 ? Math.round(totalWeight / variantCount) : 0;

                // Use primary300 color from theme for progress bar
                const progressColor = primaryShades.primary300; // Use primary300 color for all progress bars

                return (
                    <div
                        style={{
                            backgroundColor: "white",
                            color: "#333",
                            padding: "clamp(0.2rem, 0.15rem + 0.25vw, 0.25rem) clamp(0.35rem, 0.3rem + 0.5vw, 0.5rem)",
                            borderRadius: "0.25rem",
                            fontSize: "clamp(0.7rem, 0.65rem + 0.15vw, 0.75rem)",
                            fontWeight: "bold",
                            maxWidth: "clamp(100px, 10rem, 130px)",
                            textAlign: "center",
                            marginLeft: 'auto',
                            marginRight: '15px',
                            position: "relative",
                            overflow: "hidden",
                            border: "1px solid #ddd"
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: `${progressPercentage}%`,
                                height: "100%",
                                backgroundColor: progressColor,
                                zIndex: 1,
                                transition: "width 0.3s ease"
                            }}
                        ></div>
                        <span style={{ position: "relative", zIndex: 2, color: "#333" }}>{progressPercentage}%</span>
                    </div>
                );
            },
            align: 'right',
            width: '17%',
        },
    ];

    // Funzione per verificare se un campo ha un match con il termine di ricerca
    const hasSearchMatch = (fieldValue, searchTerm) => {
        return searchTerm.trim() !== '' &&
            fieldValue &&
            fieldValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
    };

    // Funzione per applicare la ricerca
    const handleApplySearch = () => {
        setAppliedSearchTerm(searchTerm);
        setIsManualExpansion(false);
    };

    // Funzione per pulire la ricerca
    const handleClearSearch = () => {
        setSearchTerm("");
        setAppliedSearchTerm("");
        setIsManualExpansion(false);
    };

    // Funzione per cancellare il testo e applicare automaticamente
    const handleClearInput = () => {
        setSearchTerm("");
        setAppliedSearchTerm("");
        setIsManualExpansion(false);
    };

    // Funzione per evidenziare il testo che fa match con la ricerca
    const highlightMatch = (text, appliedSearchTerm) => {
        if (!text || !appliedSearchTerm.trim()) return text;

        const hasMatch = hasSearchMatch(text, appliedSearchTerm);

        return (
            <span style={{
                backgroundColor: hasMatch ? PRIMARY_200 : 'transparent',
                padding: hasMatch ? '2px 4px' : '0',
                borderRadius: hasMatch ? '3px' : '0',
                fontWeight: hasMatch ? 'bold' : 'normal'
            }}>
                {text}
            </span>
        );
    };

    // Funzione che filtra i modelli in base al nome del prodotto e ai dettagli delle varianti
    const filteredModels = models.filter((model) => {
        // Se non c'è ricerca applicata, mostra tutti i modelli
        if (!appliedSearchTerm.trim()) return true;

        // Cerca nel nome del prodotto principale
        const productMatch = model.modelName.toLowerCase().includes(appliedSearchTerm.toLowerCase());

        // Se c'è già un match nel prodotto, restituisci true
        if (productMatch) return true;

        // Altrimenti, cerca nelle varianti del prodotto
        const modelVariants = variants[model.id] || [];
        const variantMatch = modelVariants.some(variant => {
            return (
                // Cerca nel nome della variante (glassesName)
                (variant.glassesName && variant.glassesName.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                // Cerca nel SKU della variante (skuModel)
                (variant.skuModel && variant.skuModel.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                // Cerca nel colore del frame
                (variant.frameColor && variant.frameColor.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                // Cerca nel colore delle lenti
                (variant.lensesColor && variant.lensesColor.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                // Cerca nella taglia
                (variant.size && variant.size.size && variant.size.size.toString().toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                // Cerca nel batch
                (variant.batch && variant.batch.toString().toLowerCase().includes(appliedSearchTerm.toLowerCase()))
            );
        });

        return variantMatch;
    });

    // Effetto per espandere automaticamente i prodotti quando c'è una ricerca attiva
    useEffect(() => {
        if (appliedSearchTerm.trim() !== '') {
            // Trova tutti i prodotti che hanno match nelle varianti (non nel nome del prodotto)
            const productsToExpand = filteredModels.filter(model => {
                // Verifica se il match è nel prodotto principale
                const productMatch = model.modelName.toLowerCase().includes(appliedSearchTerm.toLowerCase());

                // Se il match è nel prodotto, non espandere automaticamente
                if (productMatch) return false;

                // Altrimenti, controlla se c'è match nelle varianti
                const modelVariants = variants[model.id] || [];
                return modelVariants.some(variant => {
                    return (
                        (variant.glassesName && variant.glassesName.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                        (variant.skuModel && variant.skuModel.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                        (variant.frameColor && variant.frameColor.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                        (variant.lensesColor && variant.lensesColor.toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                        (variant.size && variant.size.size && variant.size.size.toString().toLowerCase().includes(appliedSearchTerm.toLowerCase())) ||
                        (variant.batch && variant.batch.toString().toLowerCase().includes(appliedSearchTerm.toLowerCase()))
                    );
                });
            });

            // Espandi tutti i prodotti che hanno match nelle varianti
            const newExpandedKeys = productsToExpand.map(product => product.id);
            setExpandedRowKeys(newExpandedKeys);
        } else {
            // Se non c'è ricerca, chiudi tutte le righe espanse solo se non c'è espansione manuale
            if (!isManualExpansion) {
                setExpandedRowKeys([]);
            }
        }
    }, [appliedSearchTerm]);

    return (
        <div>
            {brand && (
                <>
                    {/* Header Section - Logo e Nome del Brand */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', height: '64px' }}>
                                {/* Pulsante Back per tornare alla pagina precedente */}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>
                                        <HeaderComponent
                                            buttonIcon={<NavigateBeforeIcon style={{ fontSize: '1.5rem' }} />}
                                            buttonText=""
                                            onButtonClick={handleBack}
                                            isVisible={true}
                                            buttonPosition="left"
                                            noCamp={true}
                                        />
                                    </div>
                                </div>

                                {/* Nome del Brand o Ordine con possibilità di modifica */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {isEditingName ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Input
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onPressEnter={handleSaveName}
                                                style={{ fontSize: '24px', fontWeight: '600', minWidth: '300px' }}
                                                autoFocus
                                            />
                                            <Space style={{ marginLeft: '8px' }}>
                                                <Button
                                                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                                    type="primary"
                                                    shape="circle"
                                                    size="small"
                                                    icon={<CheckOutlined />}
                                                    onClick={handleSaveName}
                                                />
                                                <Button
                                                    style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: 'white' }}
                                                    shape="circle"
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={handleCancelEdit}
                                                />
                                            </Space>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <HeaderComponent
                                                title={`${brand.nameBrand}`}
                                                isVisible={false}
                                                useSpaceBetween={false}
                                                noCamp={true}
                                            />
                                            {isOrderMode && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                                                    <Tooltip title="Modifica nome" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                        <EditOutlined
                                                            onClick={handleStartEditName}
                                                            style={{
                                                                fontSize: '20px',
                                                                color: primaryShades.primary300,
                                                                cursor: 'pointer',
                                                                padding: '4px'
                                                            }}
                                                        />
                                                    </Tooltip>

                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* Icona per ordini */}
                                {!brand.urlLogo && (
                                    <div style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                        marginLeft: 'clamp(0.75rem, 0.7rem + 0.5vw, 0.9375rem)'
                                    }}>
                                        <div
                                            style={{
                                                width: '64px',
                                                height: '64px',
                                                borderRadius: '50%',
                                                backgroundColor: primaryShades.primary300,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <img
                                                src={ARShadesOrdersIcon}
                                                alt="Orders"
                                                style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    filter: 'brightness(0) invert(1)'
                                                }}
                                            />
                                        </div>

                                        {/* Icona upload/delete per ordini */}
                                        {isOrderMode && (
                                            brand.urlLogo ? (
                                                <Tooltip title="Remove Image" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                    <DeleteOutlined
                                                        onClick={handleDirectRemoveImage}
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '0px',
                                                            right: '0px',
                                                            fontSize: '16px',
                                                            color: '#ff4d4f',
                                                            cursor: 'pointer',
                                                            backgroundColor: 'white',
                                                            borderRadius: '50%',
                                                            padding: '4px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                            zIndex: 5
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Upload Image" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                    <UploadOutlined
                                                        onClick={() => {
                                                            document.getElementById(`upload-order-image-${currentOrderId}`).click();
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '0px',
                                                            right: '0px',
                                                            fontSize: '16px',
                                                            color: primaryShades.primary300,
                                                            cursor: uploadingImage ? 'not-allowed' : 'pointer',
                                                            opacity: uploadingImage ? 0.5 : 1,
                                                            backgroundColor: 'white',
                                                            borderRadius: '50%',
                                                            padding: '4px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                            zIndex: 5
                                                        }}
                                                    />
                                                </Tooltip>
                                            )
                                        )}
                                    </div>
                                )}

                                {/* Logo per brand */}
                                {brand.urlLogo && (
                                    <div style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                        marginLeft: 'clamp(0.75rem, 0.7rem + 0.5vw, 0.9375rem)'
                                    }}>
                                        <Avatar
                                            size={64}
                                            src={brand.urlLogo}
                                            alt={`${brand.nameBrand} Logo`}
                                        />

                                        {/* Icona delete per brand con logo personalizzato */}
                                        {isOrderMode && (
                                            <Tooltip title="Remove Image" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                <DeleteOutlined
                                                    onClick={handleDirectRemoveImage}
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: '0px',
                                                        right: '0px',
                                                        fontSize: '16px',
                                                        color: '#ff4d4f',
                                                        cursor: 'pointer',
                                                        backgroundColor: 'white',
                                                        borderRadius: '50%',
                                                        padding: '4px',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        zIndex: 5
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Input file nascosto per l'upload dell'immagine ordini */}
                            {isOrderMode && (
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id={`upload-order-image-${currentOrderId}`}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            handleImageSelection(file, currentOrderId);
                                        }
                                        e.target.value = null; // Reset per permettere ricaricamento dello stesso file
                                    }}
                                />
                            )}

                            {/* Bottone Publish 3D Assets - Visibile solo agli Admin */}
                            {secureGetItem("role") === "Admin" && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <ButtonState
                                        state="custom"
                                        text="Publish 3D Assets"
                                        onClick={() => setShowPublishModal(true)}
                                        style={{
                                            backgroundColor: primaryShades.primary500,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            ':hover': {
                                                backgroundColor: primaryShades.primary600,
                                                transform: 'translateY(-1px)'
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Barra di Ricerca con Bottone Apply */}
                    <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        marginBottom: "1rem",
                        width: "100%",
                        alignItems: 'center'
                    }}
                        className="search-container"
                    >
                        <div style={{
                            flex: 1,
                            position: 'relative',
                            border: '1px solid #d9d9d9',
                            borderRadius: "0.5rem",
                            padding: "clamp(0.375rem, 0.3rem + 0.5vw, 0.5rem)",
                            backgroundColor: '#fff',
                            fontSize: "clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem)",
                            transition: 'border-color 0.3s',
                            ':focus-within': {
                                borderColor: '#1890ff',
                                boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)'
                            }
                        }}>
                            <input
                                placeholder="Search by: Product Name, Variant Name, Variant SKU, Frame Color, Lens Color, Size, Batch..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleApplySearch()}
                                style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    fontSize: 'inherit',
                                    width: '100%',
                                    paddingRight: searchTerm ? '15px' : '0'
                                }}
                            />
                            {searchTerm && (
                                <Tooltip title="Clear search" placement="top" color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                    <CloseOutlined
                                        onClick={handleClearInput}
                                        style={{
                                            cursor: 'pointer',
                                            color: '#999',
                                            fontSize: '12px',
                                            position: 'absolute',
                                            right: '8px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            fontWeight: '400',
                                            opacity: '0.6',
                                            ':hover': {
                                                opacity: '1',
                                                color: '#666'
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <IconButton
                            icon={<SearchOutlined />}
                            type="primary"
                            onClick={handleApplySearch}
                            tooltip="Apply Search"
                        />

                    </div>

                    {/* Tabella Principale dei Prodotti */}
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: 'white' }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={filteredModels}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            showSorterTooltip={false}
                            scroll={{ x: 750 }}
                            expandable={{
                                // Contenuto espanso - Tabella delle Varianti del Prodotto
                                expandedRowRender: (record) => (
                                    <div style={{ padding: "clamp(0.5rem, 0.4rem + 0.5vw, 0.625rem)" }}>
                                        {/* Titolo della sezione varianti */}
                                        <Typography.Text strong style={{ display: 'block', marginBottom: 'clamp(0.5rem, 0.4rem + 0.5vw, 0.75rem)', fontSize: "clamp(0.875rem, 0.8rem + 0.25vw, 1rem)" }}>
                                            Available variants: {variants[record.id] ? variants[record.id].length : 0}
                                        </Typography.Text>

                                        {/* Tabella delle Varianti del Prodotto */}
                                        {variants[record.id] ? (
                                            <Table
                                                columns={[
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span>Image</span>
                                                                <CustomTooltip title="Image of the variant">
                                                                    <InfoCircleOutlined style={{ marginLeft: '0.5rem', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'poster',
                                                        key: 'poster',
                                                        render: (text, variant) => {
                                                            const variantImgKey = `variant_${variant.id}`;
                                                            const imgSrc = processedImages[variantImgKey] || text;

                                                            if (!processedImages[variantImgKey] && text) {
                                                                removeWhiteBackground(text, variantImgKey);
                                                            }

                                                            return (
                                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                                                    {imgSrc ? (
                                                                        <HoverImageZoom
                                                                            src={imgSrc}
                                                                            alt="Variant"
                                                                            style={{ width: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)', objectFit: 'contain' }}
                                                                        />
                                                                    ) : (
                                                                        <div style={{
                                                                            width: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)',
                                                                            height: 'clamp(2.5rem, 2rem + 2vw, 3.125rem)',
                                                                            display: 'flex',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center',
                                                                            color: '#999',
                                                                            fontSize: '0.75rem'
                                                                        }}>
                                                                            No Image
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                        align: 'center',
                                                        width: '8%',
                                                    },
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                                <span>Product Name</span>
                                                                <CustomTooltip title="Name of the product">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer', color: 'black' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'glassesName',
                                                        key: 'glassesName',
                                                        render: (text, variant) => (
                                                            <span
                                                                onClick={() => isVariantClickable(secureGetItem("role"), variant.status) ? handleVariantClick(brandId, record.id, variant.id, variant.status) : message.warning("You don't have permission to access this variant.")}
                                                                style={{ cursor: isVariantClickable(secureGetItem("role"), variant.status) ? 'pointer' : 'not-allowed', color: 'black', textDecoration: 'underline' }}
                                                            >
                                                                {highlightMatch(text, appliedSearchTerm)}
                                                            </span>
                                                        ),
                                                        align: 'left',
                                                        width: '15%',
                                                    },
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                                <span>SKU</span>
                                                                <CustomTooltip title="SKU of the model">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'skuModel',
                                                        key: 'skuModel',
                                                        render: (text, variant) => highlightMatch(text, appliedSearchTerm),
                                                        align: 'left',
                                                        width: '10%',
                                                    },
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                                <span>Frame Color</span>
                                                                <CustomTooltip title="Color of the frame">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'frameColor',
                                                        key: 'frameColor',
                                                        render: (text, variant) => highlightMatch(text, appliedSearchTerm),
                                                        align: 'left',
                                                        width: '10%',
                                                    },
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                                <span>Lenses Color</span>
                                                                <CustomTooltip title="Color of the lenses">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'lensesColor',
                                                        key: 'lensesColor',
                                                        render: (text, variant) => {
                                                            const displayText = text === "0" || !text ? "TRANSPARENT" : text;
                                                            return highlightMatch(displayText, appliedSearchTerm);
                                                        },
                                                        align: 'left',
                                                        width: '10%',
                                                    },
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span>Size</span>
                                                                <CustomTooltip title="Size of the variant">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'size',
                                                        key: 'size',
                                                        render: (size, variant) => highlightMatch(size?.size, appliedSearchTerm),
                                                        align: 'center',
                                                        width: '7%',
                                                    },
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span>Batch</span>
                                                                <CustomTooltip title="Batch number">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'batch',
                                                        key: 'batch',
                                                        render: (text, variant) => highlightMatch(text, appliedSearchTerm),
                                                        align: 'center',
                                                        width: '7%',
                                                    },
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span>Note</span>
                                                                <CustomTooltip title="Notes for the variant">
                                                                    <InfoCircleOutlined style={{ marginLeft: 'clamp(0.5rem, 0.4rem + 0.25vw, 0.625rem)', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'list_notes',
                                                        key: 'note',
                                                        render: (list_notes = [], record) => (
                                                            canViewNotes(secureGetItem("role"), record.status) ? (
                                                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                                    <NoteViewerModal list_notes={list_notes} record={record} />
                                                                </div>
                                                            ) : (
                                                                <div style={{ color: 'gray', textAlign: 'center' }}>{list_notes?.length || 0}</div>
                                                            )
                                                        ),
                                                        align: 'center',
                                                        width: '8%',
                                                    },
                                                    // Colonna "Assigned To" visibile solo per Admin e ModellerSupervisor
                                                    ...(secureGetItem("role") === "Admin" || secureGetItem("role") === "ModellerSupervisor" ? [{
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span>Assigned To</span>
                                                                <CustomTooltip title="Person assigned to this variant">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'assignedTo',
                                                        key: 'assignedTo',
                                                        render: (text) => {
                                                            if (!text || text.trim() === '') {
                                                                return (
                                                                    <div style={{
                                                                        textAlign: 'center',
                                                                        color: 'black'
                                                                    }}>
                                                                        Not Assigned
                                                                    </div>
                                                                );
                                                            }

                                                            // Cerca il profilo corrispondente all'email
                                                            const profile = profiles[text];
                                                            const displayName = profile ? profile.fullName : text;

                                                            return (
                                                                <div style={{
                                                                    textAlign: 'center',
                                                                    color: 'black'
                                                                }}>
                                                                    {displayName}
                                                                </div>
                                                            );
                                                        },
                                                        sorter: {
                                                            compare: (a, b) => {
                                                                const assignedA = a.assignedTo && a.assignedTo.trim() !== '';
                                                                const assignedB = b.assignedTo && b.assignedTo.trim() !== '';

                                                                if (assignedA === assignedB) {
                                                                    // Se entrambi sono assegnati o entrambi non sono assegnati, ordina per nome
                                                                    if (!assignedA) return 0;

                                                                    const profileA = profiles[a.assignedTo];
                                                                    const profileB = profiles[b.assignedTo];

                                                                    const nameA = profileA ? profileA.fullName : a.assignedTo;
                                                                    const nameB = profileB ? profileB.fullName : b.assignedTo;

                                                                    return nameA.localeCompare(nameB);
                                                                }

                                                                // Gli assegnati vengono prima dei non assegnati
                                                                return assignedA ? -1 : 1;
                                                            },
                                                            multiple: 1
                                                        },
                                                        align: 'center',
                                                        width: '12%',
                                                    }] : []),
                                                    {
                                                        title: (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                <span>Status</span>
                                                                <CustomTooltip title="Status of the variant">
                                                                    <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                                                </CustomTooltip>
                                                            </div>
                                                        ),
                                                        dataIndex: 'status',
                                                        key: 'status',
                                                        sorter: {
                                                            compare: (a, b) => {
                                                                const order = ['In Pubblicazione', 'Rev. Cliente', 'Rev. Spaarkly', 'Rev. Modellista', 'Incompleto'];
                                                                return order.indexOf(a.status) - order.indexOf(b.status);
                                                            },
                                                            multiple: 1
                                                        },
                                                        render: (text, variant) => {
                                                            const statusTranslations = {
                                                                "In Pubblicazione": "In Publication",
                                                                "Rev. Modellista": "Modelist Rev.",
                                                                "Rev. Cliente": "Client Rev.",
                                                                "Rev. Spaarkly": "Spaarkly Rev.",
                                                                "Incompleto": "Incomplete"
                                                            };
                                                            const translatedStatus = statusTranslations[text] || text;
                                                            return (
                                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                    <Tooltip title={translatedStatus} color="#000" overlayInnerStyle={{ color: '#fff' }}>
                                                                        <ButtonState
                                                                            state={translatedStatus}
                                                                            text={translatedStatus}
                                                                            style={{
                                                                                width: '100%',
                                                                                maxWidth: '160px'
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </div>
                                                            );
                                                        },
                                                        showSorterTooltip: false,
                                                        width: '15%',
                                                        align: 'right',
                                                    },
                                                ]}
                                                dataSource={variants[record.id]
                                                    .filter(variant => variant.status !== 'Pubblicato')
                                                    .sort((a, b) => {
                                                        const order = ['In Pubblicazione', 'Rev. Cliente', 'Rev. Spaarkly', 'Rev. Modellista', 'Incompleto'];
                                                        return order.indexOf(a.status) - order.indexOf(b.status);
                                                    })}
                                                rowKey="id"
                                                pagination={false}
                                                size="small"
                                                showSorterTooltip={false}
                                                scroll={{ x: 980 }}
                                            />
                                        ) : (
                                            // Loader durante il caricamento delle varianti
                                            <Spin />
                                        )}
                                    </div>
                                ),
                                expandedRowKeys: expandedRowKeys,
                                onExpand: async (expanded, record) => {
                                    // Segna che c'è stata un'espansione manuale
                                    setIsManualExpansion(true);

                                    if (expanded && !variants[record.id]) {
                                        await fetchVariants(record.id);
                                    }
                                    setExpandedRowKeys(expanded
                                        ? [...expandedRowKeys, record.id]
                                        : expandedRowKeys.filter(key => key !== record.id)
                                    );
                                },
                            }}
                        />
                    )}
                </>
            )}

            {/* Mostra il modal di pubblicazione 3D Assets e gestisce il ricaricamento automatico dei dati
                 quando il modal viene chiuso, aggiornando la lista prodotti e varianti per riflettere
                 i cambiamenti di stato dopo la pubblicazione delle varianti selezionate. */}
            <Publish3DAssetsModal
                visible={showPublishModal}
                onClose={() => {
                    setShowPublishModal(false);
                    // Ricarica i dati dopo la pubblicazione
                    if (brandId) {
                        const extractedBrandId = brandId.startsWith("id=") ? brandId.replace("id=", "") : brandId;
                        const fetchBrandAndModels = async () => {
                            try {
                                // Ricarica i profili
                                await fetchProfiles();

                                const q = query(
                                    collection(db, "Products"),
                                    where("mainBrandRef", "==", extractedBrandId)
                                );
                                const querySnapshot = await getDocs(q);
                                const filteredModels = querySnapshot.docs
                                    .map(doc => ({ id: doc.id, ...doc.data() }))
                                    .map(product => ({
                                        ...product,
                                        status: statusMapping[product.status] || product.status
                                    }))
                                    .sort((a, b) => {
                                        const order = ['Published', 'In Publication', 'Client Rev.', 'Spaarkly Rev.', 'Modelist Rev.', 'Incomplete'];
                                        return order.indexOf(a.status) - order.indexOf(b.status);
                                    });

                                const variantsData = {};
                                await Promise.all(filteredModels.map(async (product) => {
                                    const productVariants = await fetchVariants(product.id);
                                    variantsData[product.id] = productVariants;
                                }));

                                // Filtra i prodotti che hanno almeno una variante non pubblicata
                                const productsWithVariants = filteredModels.filter(product => {
                                    const productVariants = variantsData[product.id] || [];
                                    const nonPublishedVariants = productVariants.filter(variant => variant.status !== "Pubblicato");
                                    return nonPublishedVariants.length > 0;
                                });

                                setVariants(variantsData);
                                setModels(productsWithVariants);
                            } catch (error) {
                                console.error("Errore nel ricaricamento dei dati:", error);
                            }
                        };
                        fetchBrandAndModels();
                    }
                }}
                brandId={brandId}
            />

            {/* Modale per il posizionamento dell'immagine */}
            <ImagePositioningModal
                visible={isImageModalVisible}
                onCancel={handleImageModalCancel}
                onConfirm={handleImageModalConfirm}
                imageFile={selectedImageFile}
                loading={uploadingImage}
            />
        </div>
    );
}

export default Products;